/**
 * Fill-in-the-Blank Answer Checking Utility
 * 
 * Provides flexible matching for fill-in-blank questions with:
 * - String normalization
 * - Partial matching (contains)
 * - Token overlap matching
 * - Auto-derived acceptable answers from common patterns
 */

// Common stopwords to exclude from token matching
const STOPWORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
  'of', 'with', 'by', 'from', 'is', 'are', 'was', 'were', 'be', 'been',
  'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would',
  'could', 'should', 'may', 'might', 'must', 'shall', 'can', 'need',
  'it', 'its', 'this', 'that', 'these', 'those', 'as', 'so', 'than'
]);

/**
 * Normalize a string for comparison
 * - Trim whitespace
 * - Convert to lowercase
 * - Remove punctuation and special characters
 * - Convert multiple spaces to single space
 */
export function normalizeAnswer(answer) {
  if (!answer) return '';
  return answer
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[.,;:!?'"()[\]{}<>\/\\@#$%^&*_+=~`|]/g, '') // Remove punctuation
    .replace(/-/g, ' ') // Convert hyphens to spaces
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
}

/**
 * Extract meaningful tokens from a string (excluding stopwords)
 */
function getTokens(str) {
  const normalized = normalizeAnswer(str);
  if (!normalized) return [];
  return normalized.split(' ').filter(token => 
    token.length > 0 && !STOPWORDS.has(token)
  );
}

/**
 * Calculate token overlap ratio
 * Returns the percentage of correctAnswer tokens found in userAnswer
 */
function calculateTokenOverlap(userAnswer, correctAnswer) {
  const userTokens = new Set(getTokens(userAnswer));
  const correctTokens = getTokens(correctAnswer);
  
  if (correctTokens.length === 0) return 0;
  
  let matchCount = 0;
  for (const token of correctTokens) {
    if (userTokens.has(token)) {
      matchCount++;
    }
  }
  
  return matchCount / correctTokens.length;
}

/**
 * Auto-derive acceptable answers from common patterns
 * e.g., "VA (Veno-Arterial)" → ["va", "venoarterial", "veno arterial", "veno-arterial"]
 */
export function deriveAcceptableAnswers(correctAnswer) {
  if (!correctAnswer) return [];
  
  const derived = [];
  const str = correctAnswer.toString();
  
  // Pattern: "ABBR (Full Name)" - extract abbreviation and full name
  const parenMatch = str.match(/^([^(]+)\s*\(([^)]+)\)$/);
  if (parenMatch) {
    const abbr = parenMatch[1].trim();
    const fullName = parenMatch[2].trim();
    
    derived.push(abbr);
    derived.push(fullName);
    
    // Also add versions without hyphens/spaces
    derived.push(fullName.replace(/-/g, ''));
    derived.push(fullName.replace(/-/g, ' '));
    derived.push(fullName.replace(/\s+/g, ''));
  }
  
  // Pattern: "word-word" - add variants
  if (str.includes('-')) {
    derived.push(str.replace(/-/g, ''));
    derived.push(str.replace(/-/g, ' '));
  }
  
  // Pattern: "word/word" - split alternatives
  if (str.includes('/')) {
    const parts = str.split('/');
    parts.forEach(part => derived.push(part.trim()));
  }
  
  return derived;
}

/**
 * Main function to check if a fill-in-blank answer is correct
 * 
 * @param {string} userAnswer - The user's answer
 * @param {string} correctAnswer - The correct answer
 * @param {string[]} acceptableAnswers - Optional array of additional acceptable answers
 * @returns {boolean} - Whether the answer should be marked correct
 */
export function isFillBlankCorrect(userAnswer, correctAnswer, acceptableAnswers = []) {
  // Normalize inputs
  const normalizedUser = normalizeAnswer(userAnswer);
  const normalizedCorrect = normalizeAnswer(correctAnswer);
  
  // Guardrail: If normalized user answer is too short (< 2 chars), always incorrect
  if (normalizedUser.length < 2) {
    return false;
  }
  
  // Build complete list of acceptable answers (including auto-derived)
  const allAcceptable = [
    correctAnswer,
    ...deriveAcceptableAnswers(correctAnswer),
    ...(acceptableAnswers || [])
  ];
  
  // Check against all acceptable answers
  for (const acceptable of allAcceptable) {
    const normalizedAcceptable = normalizeAnswer(acceptable);
    if (!normalizedAcceptable) continue;
    
    // Rule 1: Exact match after normalization
    if (normalizedUser === normalizedAcceptable) {
      return true;
    }
  }
  
  // Guardrail: For short correct answers (abbreviations, ≤4 chars), 
  // only allow exact match or acceptableAnswers match (no partial matching)
  if (normalizedCorrect.length <= 4) {
    // Already checked exact matches above, so return false for short answers
    return false;
  }
  
  // Rule 2: Contains matching (for answers longer than abbreviations)
  // User answer contains correct answer OR correct answer contains user answer
  for (const acceptable of allAcceptable) {
    const normalizedAcceptable = normalizeAnswer(acceptable);
    if (!normalizedAcceptable || normalizedAcceptable.length <= 4) continue;
    
    // User answer contains the acceptable answer
    if (normalizedUser.includes(normalizedAcceptable)) {
      return true;
    }
    
    // Acceptable answer contains the user answer (only if user answer is meaningful)
    if (normalizedAcceptable.includes(normalizedUser) && normalizedUser.length >= 3) {
      return true;
    }
  }
  
  // Rule 3: Token overlap (70% threshold)
  // Only for longer answers (not abbreviations)
  const tokenOverlap = calculateTokenOverlap(normalizedUser, normalizedCorrect);
  if (tokenOverlap >= 0.7) {
    return true;
  }
  
  return false;
}

/**
 * Legacy function for backwards compatibility
 * Wraps isFillBlankCorrect
 */
export function checkFillInBlankAnswer(userAnswer, correctAnswer, acceptableAnswers = []) {
  return isFillBlankCorrect(userAnswer, correctAnswer, acceptableAnswers);
}

export default isFillBlankCorrect;
